import { useCallback, useEffect, useRef, useState } from 'react'

import { migrateLegacyPushWorker } from '@/lib/pwa/push'

import {
  WebPushError,
  disableWebPush,
  enableWebPush,
  fetchPushPublicKey,
  getExistingPushSubscription,
  pushPermission,
  webPushSupported,
} from '../push/web-push'

export type PushKeyState = 'unavailable' | 'checking' | 'available' | 'missing' | 'error'
export type PushBusy = 'enabling' | 'disabling' | null

export interface PushStatusMessage {
  tone: 'success' | 'error' | 'info'
  messageKey: string
}

export interface WebPushState {
  supported: boolean
  permission: NotificationPermission | 'unsupported'
  subscribed: boolean
  keyState: PushKeyState
  busy: PushBusy
  status: PushStatusMessage | null
  enable: () => Promise<void>
  disable: () => Promise<void>
  refresh: () => void
  dismissStatus: () => void
}

function permissionAfterError(error: unknown): NotificationPermission | null {
  if (error instanceof WebPushError && error.code === 'denied') {
    return 'denied'
  }
  return null
}

/**
 * Device-level web-push state for the notifications screen.
 *
 * Mounting never requests permission: it only reads the existing permission,
 * migrates a legacy `/push/` subscription (f472f9a) and checks whether the
 * root-scope worker already has a subscription. The browser prompt is reachable
 * exclusively through `enable()`, wired to the explicit button.
 */
export function useWebPush(): WebPushState {
  const supportedInitially = webPushSupported()
  const [supported, setSupported] = useState(supportedInitially)
  const [permission, setPermission] = useState<
    NotificationPermission | 'unsupported'
  >(supportedInitially ? pushPermission() : 'unsupported')
  const [subscribed, setSubscribed] = useState(false)
  const [keyState, setKeyState] = useState<PushKeyState>(
    supportedInitially ? 'checking' : 'unavailable',
  )
  const [busy, setBusy] = useState<PushBusy>(null)
  const [status, setStatus] = useState<PushStatusMessage | null>(null)
  const [attempt, setAttempt] = useState(0)
  const mountedRef = useRef(true)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  const refresh = useCallback(() => {
    if (webPushSupported()) setKeyState('checking')
    setAttempt((value) => value + 1)
  }, [])

  useEffect(() => {
    if (!webPushSupported()) return

    let cancelled = false
    const load = async () => {
      // Keep the f472f9a migration behavior: safe to call repeatedly and it
      // never prompts for permission.
      await migrateLegacyPushWorker()
      if (cancelled) return

      const [subscription, key] = await Promise.allSettled([
        getExistingPushSubscription(),
        fetchPushPublicKey(),
      ])
      if (cancelled) return

      setSubscribed(
        subscription.status === 'fulfilled' && subscription.value !== null,
      )
      if (key.status === 'fulfilled') {
        setKeyState(key.value ? 'available' : 'missing')
      } else {
        setKeyState('error')
      }
    }

    void load()

    return () => {
      cancelled = true
    }
  }, [attempt])

  useEffect(() => {
    if (!webPushSupported()) return

    const sync = () => {
      setPermission(pushPermission())
      void getExistingPushSubscription().then((subscription) => {
        if (mountedRef.current) setSubscribed(subscription !== null)
      })
    }
    const onVisibility = () => {
      if (document.visibilityState === 'visible') sync()
    }

    window.addEventListener('focus', sync)
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      window.removeEventListener('focus', sync)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [])

  const enable = useCallback(async () => {
    if (busy) return
    setBusy('enabling')
    setStatus(null)
    try {
      await enableWebPush()
      if (!mountedRef.current) return
      setSupported(true)
      setPermission('granted')
      setSubscribed(true)
      setKeyState('available')
      setStatus({
        tone: 'success',
        messageKey: 'notifications.push.enableSuccess',
      })
    } catch (error) {
      if (!mountedRef.current) return
      const denied = permissionAfterError(error)
      if (denied) {
        setPermission(denied)
      }
      if (error instanceof WebPushError) {
        if (error.code === 'unsupported') {
          setSupported(false)
          setPermission('unsupported')
          setKeyState('unavailable')
        } else if (error.code === 'disabled') {
          setKeyState('missing')
        } else if (error.code === 'error') {
          setStatus({
            tone: 'error',
            messageKey: 'notifications.push.enableError',
          })
        }
      } else {
        setStatus({
          tone: 'error',
          messageKey: 'notifications.push.enableError',
        })
      }
    } finally {
      if (mountedRef.current) setBusy(null)
    }
  }, [busy])

  const disable = useCallback(async () => {
    if (busy) return
    setBusy('disabling')
    setStatus(null)
    try {
      await disableWebPush()
      if (!mountedRef.current) return
      setSubscribed(false)
      setStatus({
        tone: 'success',
        messageKey: 'notifications.push.disableSuccess',
      })
    } catch {
      if (!mountedRef.current) return
      setStatus({
        tone: 'error',
        messageKey: 'notifications.push.disableError',
      })
    } finally {
      if (mountedRef.current) setBusy(null)
    }
  }, [busy])

  const dismissStatus = useCallback(() => setStatus(null), [])

  return {
    supported,
    permission,
    subscribed,
    keyState,
    busy,
    status,
    enable,
    disable,
    refresh,
    dismissStatus,
  }
}
